/**
 * Reports tool executor hook
 *
 * Executes reports tools by interacting with the reports store
 */

import { DateTime } from "luxon";
import { useCallback } from "react";
import { useReportsV2Store } from "@/domains/reports-v2/store/use-reports-v2-store";
import { ComparisonStrategy, TimeRangePreset } from "@/domains/reports-v2/types";

type ToolExecutionResult =
  | {
      error: string;
    }
  | {
      message?: string;
      data?: unknown;
    };

/**
 * Parse relative date strings and preset names
 */
function parseTimeRangePreset(preset: string): TimeRangePreset | null {
  const presetMap: Record<string, TimeRangePreset> = {
    "last 7 days": TimeRangePreset.LAST_7_DAYS,
    "last 30 days": TimeRangePreset.LAST_30_DAYS,
    "last 90 days": TimeRangePreset.LAST_90_DAYS,
    "last week": TimeRangePreset.LAST_WEEK,
    "last month": TimeRangePreset.LAST_MONTH,
    "last quarter": TimeRangePreset.LAST_QUARTER,
    "last year": TimeRangePreset.LAST_YEAR,
  };

  const normalized = preset.toLowerCase().trim();
  return presetMap[normalized] || null;
}

/**
 * Parse comparison strategy
 */
function parseComparisonStrategy(strategy: string): ComparisonStrategy | null {
  const strategyMap: Record<string, ComparisonStrategy> = {
    "previous period": ComparisonStrategy.PREVIOUS_PERIOD,
    "same period last year": ComparisonStrategy.SAME_PERIOD_LAST_YEAR,
    "year over year": ComparisonStrategy.SAME_PERIOD_LAST_YEAR,
    custom: ComparisonStrategy.CUSTOM,
  };

  const normalized = strategy.toLowerCase().trim();
  return strategyMap[normalized] || null;
}

/**
 * Parse date string to Date object
 */
function parseDate(dateStr: string): Date | null {
  // Try parsing as ISO date
  const isoDate = DateTime.fromISO(dateStr);
  if (isoDate.isValid) return isoDate.toJSDate();

  // Try parsing as common formats
  const formats = ["yyyy-MM-dd", "dd/MM/yyyy", "MM/dd/yyyy"];
  for (const format of formats) {
    const parsed = DateTime.fromFormat(dateStr, format);
    if (parsed.isValid) return parsed.toJSDate();
  }

  return null;
}

export function useReportsToolExecutor() {
  const setTimeRangePreset = useReportsV2Store((state) => state.setTimeRangePreset);
  const setCustomTimeRange = useReportsV2Store((state) => state.setCustomTimeRange);
  const setComparisonStrategy = useReportsV2Store((state) => state.setComparisonStrategy);
  const setCustomComparisonRange = useReportsV2Store((state) => state.setCustomComparisonRange);
  const _currentPeriod = useReportsV2Store((state) => state.currentPeriod);
  const _comparisonPeriod = useReportsV2Store((state) => state.comparisonPeriod);

  /**
   * Execute setReportsDateRange tool
   */
  const executeSetDateRange = useCallback(
    (args: {
      preset?: string;
      startDate?: string;
      endDate?: string;
      comparisonStrategy?: string;
      comparisonStartDate?: string;
      comparisonEndDate?: string;
    }): ToolExecutionResult => {
      try {
        // Handle preset-based range
        if (args.preset) {
          const preset = parseTimeRangePreset(args.preset);
          if (!preset) {
            return {
              error: `Invalid preset: ${args.preset}. Valid options: last 7 days, last 90 days, last week, last month, last quarter, last year`,
            };
          }

          setTimeRangePreset(preset);

          // Optionally set comparison strategy
          if (args.comparisonStrategy) {
            const strategy = parseComparisonStrategy(args.comparisonStrategy);
            if (strategy) {
              setComparisonStrategy(strategy);
            }
          }

          const state = useReportsV2Store.getState();
          const currentPeriod = state.currentPeriod;
          const comparisonPeriod = state.comparisonPeriod;

          return {
            message: `Set reports to ${args.preset}`,
            data: {
              preset: args.preset,
              currentPeriod: currentPeriod
                ? {
                    startDate: DateTime.fromJSDate(currentPeriod.startDate).toFormat("yyyy-MM-dd"),
                    endDate: DateTime.fromJSDate(currentPeriod.endDate).toFormat("yyyy-MM-dd"),
                  }
                : null,
              comparisonPeriod: comparisonPeriod
                ? {
                    startDate: DateTime.fromJSDate(comparisonPeriod.startDate).toFormat(
                      "yyyy-MM-dd"
                    ),
                    endDate: DateTime.fromJSDate(comparisonPeriod.endDate).toFormat("yyyy-MM-dd"),
                  }
                : null,
            },
          };
        }

        // Handle custom date range
        if (args.startDate && args.endDate) {
          const startDate = parseDate(args.startDate);
          const endDate = parseDate(args.endDate);

          if (!startDate || !endDate) {
            return {
              error: `Invalid date format. Use ISO format (YYYY-MM-DD) for startDate and endDate.`,
            };
          }

          if (startDate > endDate) {
            return {
              error: `Start date must be before or equal to end date.`,
            };
          }

          setCustomTimeRange(startDate, endDate);

          // Optionally set custom comparison range
          if (args.comparisonStartDate && args.comparisonEndDate) {
            const comparisonStart = parseDate(args.comparisonStartDate);
            const comparisonEnd = parseDate(args.comparisonEndDate);

            if (!comparisonStart || !comparisonEnd) {
              return {
                error: `Invalid comparison date format. Use ISO format (YYYY-MM-DD).`,
              };
            }

            if (comparisonStart > comparisonEnd) {
              return {
                error: `Comparison start date must be before or equal to end date.`,
              };
            }

            setCustomComparisonRange(comparisonStart, comparisonEnd);
          } else if (args.comparisonStrategy) {
            const strategy = parseComparisonStrategy(args.comparisonStrategy);
            if (strategy) {
              setComparisonStrategy(strategy);
            }
          }

          return {
            message: `Set custom date range: ${DateTime.fromJSDate(startDate).toFormat("MMM dd")} - ${DateTime.fromJSDate(endDate).toFormat("MMM dd, yyyy")}`,
            data: {
              currentPeriod: {
                startDate: DateTime.fromJSDate(startDate).toFormat("yyyy-MM-dd"),
                endDate: DateTime.fromJSDate(endDate).toFormat("yyyy-MM-dd"),
              },
            },
          };
        }

        return {
          error: `Missing required parameters. Provide either 'preset' OR both 'startDate' and 'endDate'.`,
        };
      } catch (error) {
        return {
          error: error instanceof Error ? error.message : "Failed to set date range",
        };
      }
    },
    [setTimeRangePreset, setCustomTimeRange, setComparisonStrategy, setCustomComparisonRange]
  );

  /**
   * Main execution function that routes to the appropriate tool
   */
  const executeTool = useCallback(
    (toolName: string, args: Record<string, unknown>): ToolExecutionResult => {
      switch (toolName) {
        case "setReportsDateRange":
          return executeSetDateRange(args as Parameters<typeof executeSetDateRange>[0]);
        default:
          return {
            error: `Unknown tool: ${toolName}`,
          };
      }
    },
    [executeSetDateRange]
  );

  return { executeTool };
}
